import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  UserPlus,
  Mic,
  CalendarPlus,
  Send,
  Plane,
  Mail,
  CheckSquare,
  FileDown,
  Users,
  Eye,
  ArrowRight,
  Lightbulb,
  ShieldCheck,
  Copy,
  Check,
} from 'lucide-react';
import { useTutorial } from '../../contexts/TutorialContext';
import { TUTORIAL_TRACKS } from '../../constants/tutorialConstants';
import { listEmailAddresses } from '../../services/emailAddressService';

/**
 * SpeakerBureauGuide
 *
 * Standalone Getting Started guide for speaker bureau organizations.
 * Replaces the old step-by-step overlay walkthrough for org admins: a single
 * visually rich page covering the setup sequence and the platform's features,
 * viewable anytime from the sidebar (auto-opened once for new org admins).
 */

const SETUP_STEPS = [
  {
    icon: UserPlus,
    title: 'Invite your meeting planners',
    adminOnly: true,
    description:
      'Build your planning team first. Each planner gets their own login and works from the same shared speaker roster.',
    details: [
      'Go to Admin → Users & Invites and click "+ Invite User"',
      'Enter their email and keep the Meeting Planner role',
      'Check "Organization Admin" for anyone who should help manage the team',
      'Invitations are approved automatically — planners can sign up right away',
    ],
    cta: { label: 'Open Users & Invites', to: '/admin' },
  },
  {
    icon: Mic,
    title: 'Add your speakers',
    description:
      'Create a profile for each speaker: name, credentials, specialty, affiliation, and contact details. The roster is shared, so every planner sees the same up-to-date list.',
    details: [
      'Go to Speakers and click "Add Speaker"',
      'Include an email address — you\'ll need it to invite them to their portal',
      'Specialty and affiliation help planners find the right speaker fast',
    ],
    cta: { label: 'Add a Speaker', to: '/executives?action=new-speaker' },
  },
  {
    icon: CalendarPlus,
    title: 'Create a speaker program',
    description:
      'A program connects a speaker to an engagement and creates a trip — the single place where all of that engagement\'s travel lives.',
    details: [
      'On the Speakers page, click "New Program"',
      'Pick the speaker, name the engagement, and set the dates',
      'You\'ll land in the trip itinerary, ready to add travel',
    ],
    cta: { label: 'Start a Program', to: '/executives?action=new-program' },
  },
  {
    icon: Plane,
    title: 'Build the itinerary',
    description:
      'Add flights, hotels, and ground transfers to the program\'s trip. Or skip the typing: forward booking confirmations by email and they\'re parsed and filed automatically.',
    details: [
      'Add components manually from the trip page, or',
      'Forward airline / hotel confirmation emails to your organization\'s parsing address',
      'Imported reservations are matched to the right speaker and trip',
    ],
    showParsingEmail: true,
    cta: { label: 'View Trips', to: '/trips' },
  },
  {
    icon: Mail,
    title: 'Set up personal inbox addresses',
    eachPlanner: true,
    description:
      'The organization address above works for the whole team. Each planner can also allocate their own personal addresses, so anything they forward is automatically credited to them.',
    details: [
      'Go to Utilities → Email Settings and click "Allocate Email Addresses"',
      'Standard inbox — imports are visible to you and your team',
      'Confidential inbox — visible only to you, supervisors, and org admins',
      'Every planner sets up their own from the same page',
    ],
    cta: { label: 'Open Email Settings', to: '/email-settings' },
  },
  {
    icon: Send,
    title: 'Invite speakers to their portal',
    description:
      'Give each speaker a free, view-only login showing their upcoming engagements and full itinerary — no more emailing PDFs back and forth.',
    details: [
      'Open the ⋯ menu on a speaker\'s card and choose "Invite to portal"',
      'The speaker gets an email link to create their view-only account',
      'They see their engagements and itineraries, and can download PDFs',
    ],
    cta: { label: 'Open Speakers', to: '/executives' },
  },
];

const FEATURES = [
  {
    icon: Users,
    title: 'Shared speaker roster',
    description: 'One org-wide list of speakers. Every planner sees the same profiles — no duplicates, no silos.',
  },
  {
    icon: Plane,
    title: 'Complete itineraries',
    description: 'Flights, hotels, car service, and more — every travel detail for a program in one timeline.',
  },
  {
    icon: Mail,
    title: 'Email import',
    description: 'Forward booking confirmations and they\'re parsed automatically into the right trip.',
  },
  {
    icon: CheckSquare,
    title: 'Task management',
    description: 'Track everything that needs doing per program with todos and deadlines.',
  },
  {
    icon: FileDown,
    title: 'PDF itineraries',
    description: 'Export a polished, speaker-friendly PDF of any itinerary to share or print.',
  },
  {
    icon: Eye,
    title: 'Speaker portal',
    description: 'Speakers self-serve their travel details through a secure, view-only portal.',
  },
];

const TIPS = [
  'Add an email address to every speaker profile so portal invites are one click away.',
  'Forward confirmation emails to your parsing address as soon as bookings are made — the itinerary builds itself.',
  'The whole team shares the roster: update a speaker once and everyone is current.',
];

export default function SpeakerBureauGuide() {
  const { startTutorial, tutorialEnabledForOrg } = useTutorial();

  // The organization's shared parsing inbox ("general" inbound address) —
  // shown in the itinerary step so planners know where to forward confirmations
  const [parsingEmail, setParsingEmail] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listEmailAddresses()
      .then((result) => {
        if (cancelled) return;
        const addresses = result?.inbound_email_addresses || result?.email_addresses || [];
        const general = addresses.find((a) => a.email_type === 'general');
        if (general?.email_address) setParsingEmail(general.email_address);
      })
      .catch(() => { /* guide still renders with the Email Settings fallback */ });
    return () => { cancelled = true; };
  }, []);

  const handleCopyParsingEmail = async () => {
    try {
      await navigator.clipboard.writeText(parsingEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-600 via-rose-500 to-orange-400 px-6 py-10 text-white sm:px-10 sm:py-14">
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10" aria-hidden="true" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> Getting started guide
          </span>
          <h1 className="mt-4 text-3xl font-bold sm:text-4xl">
            Welcome to your speaker bureau
          </h1>
          <p className="mt-3 max-w-2xl text-base text-rose-50 sm:text-lg">
            Everything your team needs to manage speakers, programs, and travel — in one place.
            Six steps take you from an empty workspace to speakers walking on stage.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-rose-700 shadow-sm hover:bg-rose-50"
            >
              <UserPlus className="h-4 w-4" /> Invite your first planner
            </Link>
            {tutorialEnabledForOrg && (
              <button
                onClick={() => startTutorial(TUTORIAL_TRACKS.MEETING_PLANNER)}
                className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
              >
                Take the interactive tour <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Setup steps */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-slate-800">Set up in six steps</h2>
        <p className="mt-1 text-slate-500">
          Follow these in order — each step unlocks the next.
        </p>

        <ol className="mt-6 space-y-5">
          {SETUP_STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <li
                key={step.title}
                className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
                  {/* Number + icon */}
                  <div className="flex shrink-0 items-start gap-3 sm:flex-col sm:items-center">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-rose-600 to-orange-400 text-sm font-bold text-white">
                      {i + 1}
                    </span>
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-800">{step.title}</h3>
                      {step.adminOnly && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          <ShieldCheck className="h-3 w-3" /> Admin
                        </span>
                      )}
                      {step.eachPlanner && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          <Users className="h-3 w-3" /> Each planner
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600">{step.description}</p>
                    <ul className="mt-3 space-y-1.5">
                      {step.details.map((detail) => (
                        <li key={detail} className="flex items-start gap-2 text-sm text-slate-500">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-300" aria-hidden="true" />
                          {detail}
                        </li>
                      ))}
                    </ul>

                    {/* Organization's parsing inbox address */}
                    {step.showParsingEmail && (
                      parsingEmail ? (
                        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-2.5">
                          <Mail className="h-4 w-4 shrink-0 text-violet-600" />
                          <span className="text-sm font-medium text-violet-900">Your organization's parsing address:</span>
                          <code className="break-all font-mono text-sm text-violet-800">{parsingEmail}</code>
                          <button
                            onClick={handleCopyParsingEmail}
                            title="Copy address"
                            aria-label="Copy parsing email address"
                            className="ml-auto rounded-lg p-1.5 text-violet-600 hover:bg-violet-100"
                          >
                            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600">
                          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                          Find your organization's parsing address under{' '}
                          <Link to="/email-settings" className="font-semibold text-rose-600 hover:text-rose-700">
                            Utilities → Email Settings
                          </Link>
                        </div>
                      )
                    )}

                    <Link
                      to={step.cta.to}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700"
                    >
                      {step.cta.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Features */}
      <div className="mt-14">
        <h2 className="text-2xl font-bold text-slate-800">Everything your team gets</h2>
        <p className="mt-1 text-slate-500">The tools planners use every day.</p>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 font-semibold text-slate-800">{feature.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div className="mt-14 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-amber-900">
          <Lightbulb className="h-5 w-5" /> Tips from the field
        </h2>
        <ul className="mt-3 space-y-2">
          {TIPS.map((tip) => (
            <li key={tip} className="flex items-start gap-2 text-sm text-amber-800">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" aria-hidden="true" />
              {tip}
            </li>
          ))}
        </ul>
      </div>

      {/* Footer CTA */}
      <div className="mt-14 mb-6 rounded-3xl bg-slate-900 px-6 py-10 text-center text-white sm:px-10">
        <h2 className="text-2xl font-bold">Ready to get started?</h2>
        <p className="mx-auto mt-2 max-w-xl text-slate-300">
          Invite your meeting planners now — the rest of the setup takes minutes.
          You can reopen this guide anytime from the sidebar.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600"
          >
            <UserPlus className="h-4 w-4" /> Invite planners
          </Link>
          <Link
            to="/executives?action=new-speaker"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800"
          >
            <Mic className="h-4 w-4" /> Add speakers
          </Link>
        </div>
      </div>
    </div>
  );
}
